import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Slide, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useManualTouchScroll, TOUCH_SCROLL_PANEL_SX } from '../../hooks/useManualTouchScroll';
import { useOrientation } from '../../hooks/useOrientation';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { eventBus } from '../../services/EventBus';
import { refrescarSonos, refrescarSonosActivos, mapaSonos, mergeSonosSpeakers } from '../../services/sonosState';
import { requirePinIfNeeded } from '../../services/pinGate';
import {
  SonosSpeaker, SpotifyPlaylist, SpotifyPlaylistTrackRow,
  getMusicPreferences, patchMusicPreferences,
  sonosControl, playSpotifyOnSpeaker, resolveDestinationGroup, resolveCoordinatorSpeaker,
  setPlayMode, notifyQueueReordered, spotifyLightSync, queueAdd, loadSpotifyTracksIndex,
  getSpotifyPlaylistTracks, extractSpotifyPlaylistTrackItems, parseSpotifyPlaylistTrackRow,
  PlaylistSortMode, PlaylistViewMode, isSonosPlaying,
  rememberPlaylistTrackIndex, extractSpotifyPlaylistId, withPreferredAlbumArt,
} from '../../services/sonosApi';
import {
  playLocalUri, pauseLocalPlayback, resumeLocalPlayback, seekLocalPlayback,
  clearLocalPlayback, ensureLocalFollowingQueued, getLocalPlayerState,
  setLocalVolume, transferToLocalDevice, setOnLocalTrackEnded, setOnLocalTrackUriChanged,
  setOnLocalNeedFollowPrefetch, ensureLocalPlayerReady,
  localPlaybackErrorKind,
} from '../../services/spotifyWebPlayer';
import { useSpotifyWebPlayer } from '../../hooks/useSpotifyWebPlayer';
import ExpandedPlayerSheet from './ExpandedPlayerSheet';
import LiveDestinationModal, { LiveDestinationApplyResult } from './LiveDestinationModal';
import SpotifyPlaylistGrid from './SpotifyPlaylistGrid';
import PlaylistLibraryToolbar from './PlaylistLibraryToolbar';
import PlaylistDetailSheet from './PlaylistDetailSheet';
import SpotifyRecentGrid from './SpotifyRecentGrid';
import SpotifySearchPanel from './SpotifySearchPanel';
import SpotifySavedTracksSection from './SpotifySavedTracksSection';
import SonosQueueOverlay from './SonosQueueOverlay';
import LocalQueuePanel from './LocalQueuePanel';
import PlaybackSubTabs, { PlaybackSubTab } from './PlaybackSubTabs';
import { parsePlayMode, RepeatMode } from './sonosPlayMode';
import { SonosFeedbackSnackbar, SonosMessageHandler, useSonosFeedback } from './sonosFeedback';
import {
  savePlaybackDestinationKind,
  type PlaybackDestinationKind,
} from './playbackDestination';
import {
  advanceLocalQueueIndex, buildLocalQueueItem, createEmptyLocalQueue,
  peekLocalQueueFollowing, previousLocalQueueIndex, removeLocalQueueItem,
  reorderLocalQueue, shuffleLocalQueueOrder, type LocalQueueState,
} from './localQueueStore';
import {
  AMBIENT_GLOW, HEADER_GRADIENT, SURFACE_BORDER, sonosPanelTitleSx,
} from './sonosUiStyles';

interface SonosModalPlayerViewProps {
  speakerId: number;
  canControl?: boolean;
  showDeviceIp?: boolean;
  deviceIp?: string | null;
}

function applySpeakersFromApi(
  updated: SonosSpeaker[],
  setSpeakers: React.Dispatch<React.SetStateAction<SonosSpeaker[]>>,
  playbackLockRef: React.MutableRefObject<{ until: number; state: string; ids: Set<number> } | null>,
  readSpeakers: () => SonosSpeaker[],
) {
  if (!updated.length) return;
  mergeSonosSpeakers(updated);

  const lock = playbackLockRef.current;
  if (lock) {
    const groupUpdated = updated.filter((s) => lock.ids.has(s.id));
    const confirmed = groupUpdated.length > 0
      && groupUpdated.every((s) => s.playback_state === lock.state);
    if (confirmed) {
      playbackLockRef.current = null;
    } else {
      lock.ids.forEach((id) => {
        if (mapaSonos[id]) {
          mapaSonos[id] = { ...mapaSonos[id], playback_state: lock.state };
        }
      });
    }
  }

  setSpeakers(readSpeakers());
}

const SonosModalPlayerView: React.FC<SonosModalPlayerViewProps> = ({
  speakerId,
  canControl: canControlProp = false,
  showDeviceIp = false,
  deviceIp,
}) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [speakers, setSpeakers] = useState<SonosSpeaker[]>([]);
  const [coordinatorId, setCoordinatorId] = useState<number>(speakerId);
  const [crossfade, setCrossfade] = useState(true);
  const [playlistSortMode, setPlaylistSortMode] = useState<PlaylistSortMode>('alpha');
  const [playlistViewMode, setPlaylistViewMode] = useState<PlaylistViewMode>('grid');
  const [libraryFilterOpen, setLibraryFilterOpen] = useState(false);
  const [libraryFilterQuery, setLibraryFilterQuery] = useState('');
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false);
  const [playlistDetail, setPlaylistDetail] = useState<SpotifyPlaylist | null>(null);
  const [playlistsOpen, setPlaylistsOpen] = useState(false);
  const requestClosePlaylists = useOverlayBackButton(
    playlistsOpen,
    () => {
      setPlaylistDetail(null);
      setPlaylistsOpen(false);
    },
    'sonos-modal-playlists',
  );
  const [subTab, setSubTab] = useState<PlaybackSubTab>('playlists');
  const [queueOverlayOpen, setQueueOverlayOpen] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('off');
  const [liveDestOpen, setLiveDestOpen] = useState(false);
  // Vista de tarjeta de parlante: el destino es ese Sonos, no la preferencia global «este dispositivo»
  // del tab Música (si no, el player dice "Este dispositivo" mientras el modal marca el parlante).
  const [destinationKind, setDestinationKind] = useState<PlaybackDestinationKind>('sonos');
  const [localQueue, setLocalQueue] = useState<LocalQueueState>(() => createEmptyLocalQueue());
  const localQueueRef = useRef(localQueue);
  localQueueRef.current = localQueue;
  const repeatRef = useRef(repeat);
  repeatRef.current = repeat;
  const localPlayer = useSpotifyWebPlayer();
  const [loading, setLoading] = useState(() => !mapaSonos[speakerId]);
  const { snackbar, notify, closeSnackbar } = useSonosFeedback();
  const handleMessage: SonosMessageHandler = useCallback((message, severity = 'error') => {
    notify(message, severity);
  }, [notify]);
  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [transportBusy, setTransportBusy] = useState(false);
  const tracksIndexLoadedRef = useRef(false);

  const transportLockUntilRef = useRef(0);
  const playbackLockRef = useRef<{ until: number; state: string; ids: Set<number> } | null>(null);
  const volumeLockRef = useRef<{ until: number; values: Map<number, { volume: number; mute: boolean }> } | null>(null);
  const seekLockRef = useRef<{ until: number; coordinatorId: number; position: number } | null>(null);
  const liveDestLockUntilRef = useRef(0);
  const liveDestOpenRef = useRef(false);
  liveDestOpenRef.current = liveDestOpen;
  const playlistScrollRef = useManualTouchScroll(!loading && playlistsOpen);

  const readSpeakersFromMap = useCallback(() => {
    let sp = Object.values(mapaSonos).filter((s) => s.can_control);

    const lock = playbackLockRef.current;
    if (lock && Date.now() < lock.until) {
      sp = sp.map((s) => (lock.ids.has(s.id) ? { ...s, playback_state: lock.state } : s));
    } else if (lock) {
      playbackLockRef.current = null;
    }

    const volLock = volumeLockRef.current;
    if (volLock && Date.now() < volLock.until) {
      sp = sp.map((s) => {
        const v = volLock.values.get(s.id);
        return v ? { ...s, volume: v.volume, mute: v.mute } : s;
      });
    } else if (volLock) {
      volumeLockRef.current = null;
    }

    const seekLock = seekLockRef.current;
    if (seekLock && Date.now() < seekLock.until) {
      const sec = seekLock.position;
      const posStr = `${Math.floor(sec / 3600)}:${Math.floor((sec % 3600) / 60).toString().padStart(2, '0')}:${(sec % 60).toString().padStart(2, '0')}`;
      sp = sp.map((s) => (
        s.id === seekLock.coordinatorId ? { ...s, track_position: posStr } : s
      ));
    } else if (seekLock) {
      seekLockRef.current = null;
    }

    return sp;
  }, []);

  const refreshSpeakers = useCallback(async () => {
    await refrescarSonos();
    const sp = readSpeakersFromMap();
    setSpeakers(sp);
    return sp;
  }, [readSpeakersFromMap]);

  const applyOptimisticPlayback = useCallback((
    coordId: number,
    playbackState: string,
    meta?: { title?: string; artist?: string; albumArt?: string | null },
  ) => {
    const group = resolveDestinationGroup(Object.values(mapaSonos), coordId);
    playbackLockRef.current = {
      until: Date.now() + 15000,
      state: playbackState,
      ids: new Set(group.map((s) => s.id)),
    };
    mergeSonosSpeakers(group.map((s) => ({
      ...s,
      playback_state: playbackState,
      ...(meta?.title != null ? { current_title: meta.title } : {}),
      ...(meta?.artist != null ? { current_artist: meta.artist } : {}),
      ...(meta?.albumArt !== undefined ? { current_album_art_url: meta.albumArt } : {}),
    })));
    setSpeakers(readSpeakersFromMap());
  }, [readSpeakersFromMap]);

  useEffect(() => {
    setCoordinatorId(speakerId);
  }, [speakerId]);

  useEffect(() => {
    (async () => {
      try {
        const [prefs] = await Promise.all([
          getMusicPreferences().catch(() => ({
            default_crossfade: true, last_used_speaker_id: null,
            playlist_sort_mode: null, playlist_view_mode: null,
            playback_destination_kind: null as 'sonos' | 'local' | null,
          })),
          refreshSpeakers(),
        ]);
        setCrossfade(Boolean(prefs.default_crossfade));
        if (prefs.playlist_sort_mode) setPlaylistSortMode(prefs.playlist_sort_mode);
        if (prefs.playlist_view_mode) setPlaylistViewMode(prefs.playlist_view_mode);
        // No heredar «local» del tab Música: al abrir Cocina/etc. se controla ese parlante.
        setDestinationKind('sonos');
        const sp = readSpeakersFromMap();
        const coord = resolveCoordinatorSpeaker(sp, speakerId);
        setCoordinatorId(coord?.id ?? speakerId);
        // Si Spotify Web Player seguía en el teléfono, cortarlo para no contradecir el destino Sonos.
        try { await pauseLocalPlayback(); } catch { /* ignore */ }
      } finally {
        setLoading(false);
      }
    })();
  }, [speakerId, refreshSpeakers, readSpeakersFromMap]);

  useEffect(() => {
    const onUpdate = () => {
      if (liveDestOpenRef.current) return;
      setSpeakers(readSpeakersFromMap());
    };
    eventBus.on('sonos:updated', onUpdate);
    const interval = setInterval(() => {
      if (
        liveDestOpenRef.current
        || transportBusy
        || Date.now() < transportLockUntilRef.current
        || Date.now() < liveDestLockUntilRef.current
      ) return;
      void refrescarSonosActivos();
    }, 4000);
    return () => {
      eventBus.off('sonos:updated', onUpdate);
      clearInterval(interval);
    };
  }, [transportBusy, readSpeakersFromMap]);

  const coordinator = useMemo(() => {
    const list = speakers.length ? speakers : Object.values(mapaSonos);
    return resolveCoordinatorSpeaker(list, coordinatorId);
  }, [speakers, coordinatorId]);

  /** Habitación de la tarjeta: limita «dónde sonar» a parlantes de esa habitación. */
  const restrictToHabitacionId = useMemo(() => {
    const list = speakers.length ? speakers : Object.values(mapaSonos);
    const sp = list.find((s) => s.id === speakerId) ?? mapaSonos[speakerId];
    return sp?.habitacion_id ?? null;
  }, [speakers, speakerId]);

  const destinationSpeakers = useMemo(
    () => (destinationKind === 'local'
      ? []
      : resolveDestinationGroup(speakers.length ? speakers : Object.values(mapaSonos), coordinatorId)),
    [speakers, coordinatorId, destinationKind],
  );

  const playerCoordinator = useMemo(() => {
    if (destinationKind !== 'local') {
      return coordinator ? withPreferredAlbumArt(coordinator) : coordinator;
    }
    const cur = localQueue.items[localQueue.currentIndex];
    const playing = !localPlayer.paused && Boolean(localPlayer.track.uri || cur);
    return withPreferredAlbumArt({
      id: -1,
      uid: 'local-web-player',
      nombre: t('musicSystem.liveDestination.thisDevice'),
      ip: '',
      habitacion_id: null,
      artefacto_id: null,
      online: true,
      playback_state: playing ? 'playing' : (cur || localPlayer.track.uri ? 'paused' : 'idle'),
      current_title: localPlayer.track.name || cur?.title || null,
      current_artist: localPlayer.track.artist || cur?.artist || null,
      current_album: localPlayer.track.album || cur?.album || null,
      current_album_art_url: localPlayer.track.imageUrl || cur?.image_url || null,
      track_position: (() => {
        const sec = Math.floor((localPlayer.positionMs || 0) / 1000);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `0:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      })(),
      track_duration: (() => {
        const ms = localPlayer.durationMs || cur?.durationMs || 0;
        const sec = Math.floor(ms / 1000);
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `0:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      })(),
      volume: Math.round((localPlayer.volume ?? 0.8) * 100),
      mute: false,
      can_control: canControlProp,
      playing_source_name: t('musicSystem.liveDestination.thisDevice'),
    } as SonosSpeaker);
  }, [destinationKind, localQueue, localPlayer, coordinator, t, canControlProp]);

  const showPlaybackMedia = useMemo(() => {
    if (destinationKind === 'local') {
      return Boolean(
        localPlayer.track.uri
        || localPlayer.track.name
        || (localQueue.currentIndex >= 0 && localQueue.items.length > 0),
      );
    }
    return Boolean(
      coordinator && (
        isSonosPlaying(coordinator.playback_state)
        || Boolean(coordinator.current_title)
      ),
    );
  }, [destinationKind, localPlayer.track.uri, localPlayer.track.name, localQueue, coordinator]);

  const playingSourceLabel = !showPlaybackMedia
    ? null
    : (destinationKind === 'local'
      ? t('musicSystem.liveDestination.thisDevice')
      : (coordinator?.playing_source_name ?? null));
  const canControl = destinationKind === 'local'
    ? canControlProp
    : Boolean(canControlProp && coordinator?.can_control && coordinator?.online);

  useEffect(() => {
    if (destinationKind === 'local') return;
    const parsed = parsePlayMode(coordinator?.play_mode);
    setShuffle(parsed.shuffle);
    setRepeat(parsed.repeat);
  }, [destinationKind, coordinator?.play_mode, coordinator?.id]);

  const playLocalIndex = useCallback(async (index: number, queue: LocalQueueState = localQueue) => {
    const item = queue.items[index];
    if (!item?.uri) return;
    await ensureLocalPlayerReady();
    if (coordinator && isSonosPlaying(coordinator.playback_state)) {
      try { await sonosControl(coordinator.id, 'pause'); } catch { /* ignore */ }
    }
    const nextQueue = { ...queue, currentIndex: index };
    const following = peekLocalQueueFollowing(nextQueue, 2);
    await playLocalUri(item.uri, {
      followingUris: following,
      durationMs: item.durationMs,
    });
    setLocalQueue(nextQueue);
  }, [localQueue, coordinator]);

  const prefetchLocalFollowing = useCallback((queue: LocalQueueState) => {
    const following = peekLocalQueueFollowing(queue, 2);
    if (following.length) void ensureLocalFollowingQueued(following);
  }, []);

  const syncLocalQueueToUri = useCallback((uri: string) => {
    setLocalQueue((prev) => {
      if (!prev.items.length) return prev;
      const expectedIdx = advanceLocalQueueIndex({ ...prev, repeat });
      let idx = expectedIdx != null && prev.items[expectedIdx]?.uri === uri
        ? expectedIdx
        : prev.items.findIndex((it, i) => it.uri === uri && i !== prev.currentIndex);
      if (idx < 0) idx = prev.items.findIndex((it) => it.uri === uri);
      if (idx < 0 || idx === prev.currentIndex) {
        prefetchLocalFollowing(prev);
        return prev;
      }
      const next = { ...prev, currentIndex: idx };
      prefetchLocalFollowing(next);
      return next;
    });
  }, [repeat, prefetchLocalFollowing]);

  /** Solo dispositivo local: avanzar cola SHC al terminar el tema. */
  const advanceLocalAfterEnd = useCallback(() => {
    const prev = localQueueRef.current;
    const nextIdx = advanceLocalQueueIndex({ ...prev, repeat: repeatRef.current });
    if (nextIdx == null) {
      void pauseLocalPlayback();
      return;
    }
    const item = prev.items[nextIdx];
    if (!item?.uri) return;
    const playing = getLocalPlayerState();
    const next = { ...prev, currentIndex: nextIdx };
    setLocalQueue(next);
    if (playing.track.uri === item.uri && !playing.paused) {
      prefetchLocalFollowing(next);
      return;
    }
    const following = peekLocalQueueFollowing(next, 2);
    void playLocalUri(item.uri, {
      followingUris: following,
      durationMs: item.durationMs,
    }).catch(() => {
      /* best-effort */
    });
  }, [prefetchLocalFollowing]);

  useEffect(() => {
    const owner = 'sonos-modal-player';
    // No borrar callbacks del tab Audio si este modal no es destino local.
    if (destinationKind !== 'local') return undefined;
    setOnLocalTrackEnded(advanceLocalAfterEnd, owner);
    setOnLocalTrackUriChanged((uri) => { syncLocalQueueToUri(uri); }, owner);
    setOnLocalNeedFollowPrefetch(() => {
      prefetchLocalFollowing(localQueueRef.current);
    }, owner);
    return () => {
      setOnLocalTrackEnded(null, owner);
      setOnLocalTrackUriChanged(null, owner);
      setOnLocalNeedFollowPrefetch(null, owner);
    };
  }, [destinationKind, advanceLocalAfterEnd, syncLocalQueueToUri, prefetchLocalFollowing]);

  const applyPlayMode = async (nextShuffle: boolean, nextRepeat: RepeatMode) => {
    if (!canControl) return;
    if (destinationKind === 'local') {
      setShuffle(nextShuffle);
      setRepeat(nextRepeat);
      setLocalQueue((prev) => {
        let next = { ...prev, shuffle: nextShuffle, repeat: nextRepeat };
        if (nextShuffle && !prev.shuffle) next = shuffleLocalQueueOrder(next);
        else next = { ...next, shuffle: nextShuffle };
        return next;
      });
      return;
    }
    if (!coordinator) return;
    const prevShuffle = shuffle;
    const prevRepeat = repeat;
    setShuffle(nextShuffle);
    setRepeat(nextRepeat);
    try {
      const res = await setPlayMode(coordinator.id, { shuffle: nextShuffle, repeat: nextRepeat });
      // Aleatorio SHC: el backend mezcló la cola del Sonos → mostrar el orden nuevo.
      if (res?.reordered) notifyQueueReordered([coordinator.id]);
    } catch {
      setShuffle(prevShuffle);
      setRepeat(prevRepeat);
    }
  };

  const handleLocalPlayUri = useCallback(async (uri: string, opts?: { addToQueue?: boolean }) => {
    const item = buildLocalQueueItem({ uri });
    if (opts?.addToQueue) {
      setLocalQueue((prev) => ({ ...prev, items: [...prev.items, item] }));
      return;
    }
    const nextQueue: LocalQueueState = {
      items: [item],
      currentIndex: 0,
      shuffle,
      repeat,
    };
    setLocalQueue(nextQueue);
    await playLocalIndex(0, nextQueue);
  }, [playLocalIndex, shuffle, repeat]);

  const handleLocalQueueUri = useCallback(async (
    uri: string,
    meta?: { title?: string; artist?: string; album?: string; image_url?: string | null; durationMs?: number },
  ) => {
    const item = buildLocalQueueItem({
      uri,
      title: meta?.title,
      artist: meta?.artist,
      album: meta?.album,
      image_url: meta?.image_url,
      durationMs: meta?.durationMs,
    });
    setLocalQueue((prev) => {
      const next = { ...prev, items: [...prev.items, item] };
      if (prev.currentIndex >= 0) {
        const following = peekLocalQueueFollowing({ ...next, currentIndex: prev.currentIndex }, 2);
        if (following.length) void ensureLocalFollowingQueued(following);
      }
      return next;
    });
  }, []);

  const handleSortModeChange = useCallback((mode: PlaylistSortMode) => {
    setPlaylistSortMode(mode);
    void patchMusicPreferences({ playlist_sort_mode: mode }).catch(() => undefined);
  }, []);

  const handleViewModeChange = useCallback((mode: PlaylistViewMode) => {
    setPlaylistViewMode(mode);
    void patchMusicPreferences({ playlist_view_mode: mode }).catch(() => undefined);
  }, []);

  const lastLightSyncRef = useRef(0);
  const triggerLibrarySync = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastLightSyncRef.current < 120_000) return;
    lastLightSyncRef.current = now;
    const { changed } = await spotifyLightSync();
    if (changed.length > 0) {
      tracksIndexLoadedRef.current = false;
      setLibraryRefresh((k) => k + 1);
    }
  }, []);

  useEffect(() => {
    if (!playlistsOpen || subTab !== 'playlists') return undefined;
    void triggerLibrarySync();
    const id = window.setInterval(() => { void triggerLibrarySync(); }, 600_000);
    return () => window.clearInterval(id);
  }, [playlistsOpen, subTab, triggerLibrarySync]);

  useEffect(() => {
    if (!libraryFilterOpen || tracksIndexLoadedRef.current) return;
    tracksIndexLoadedRef.current = true;
    void loadSpotifyTracksIndex();
  }, [libraryFilterOpen]);

  const openLiveDestination = useCallback(() => {
    setPlaylistDetail(null);
    setQueueOverlayOpen(false);
    setLiveDestOpen(true);
  }, []);

  const openQueueOverlay = useCallback(() => {
    setPlaylistDetail(null);
    setLiveDestOpen(false);
    setQueueOverlayOpen(true);
  }, []);

  const openPlaylistDetail = useCallback((pl: SpotifyPlaylist) => {
    setQueueOverlayOpen(false);
    setLiveDestOpen(false);
    setPlaylistDetail(pl);
  }, []);

  const handleSubTabChange = useCallback((tab: PlaybackSubTab) => {
    if (tab === 'queue') {
      openQueueOverlay();
      return;
    }
    setSubTab(tab);
    if (tab !== 'playlists') {
      setLibraryFilterOpen(false);
      setLibraryFilterQuery('');
    }
    if (tab === 'playlists') {
      setLibraryRefresh((k) => k + 1);
      void triggerLibrarySync();
    }
  }, [triggerLibrarySync, openQueueOverlay]);

  const handleQueueOverlayClose = useCallback(() => {
    setQueueOverlayOpen(false);
  }, []);

  const activeSubTab: PlaybackSubTab = queueOverlayOpen ? 'queue' : subTab;

  const handleTransport = async (action: 'play' | 'pause' | 'previous' | 'next') => {
    if (destinationKind === 'local') {
      if (!canControl || transportBusy) return;
      setTransportBusy(true);
      try {
        if (action === 'play') await resumeLocalPlayback();
        else if (action === 'pause') await pauseLocalPlayback();
        else if (action === 'next') {
          const nextIdx = advanceLocalQueueIndex({ ...localQueue, repeat });
          if (nextIdx != null) await playLocalIndex(nextIdx);
        } else if (action === 'previous') {
          const prevIdx = previousLocalQueueIndex(localQueue, localPlayer.positionMs);
          if (prevIdx != null) await playLocalIndex(prevIdx);
        }
      } catch (e: any) {
        const detail = e?.response?.data?.detail || e?.message;
        const kind = localPlaybackErrorKind(e);
        if (kind === 'premium') notify(t('musicSystem.localPlayback.premiumRequired'), 'error');
        else if (kind === 'reauth') notify(t('musicSystem.localPlayback.reauthRequired'), 'error');
        else notify(detail || t('musicSystem.playback.playError'), 'error');
      } finally {
        setTransportBusy(false);
      }
      return;
    }
    if (!coordinator || transportBusy) return;
    const coordId = coordinator.id;
    const runTransport = async () => {
      if (action === 'play') applyOptimisticPlayback(coordId, 'playing');
      if (action === 'pause') applyOptimisticPlayback(coordId, 'paused');

      setTransportBusy(true);
      transportLockUntilRef.current = Date.now() + 8000;
      try {
        const updated = await sonosControl(coordId, action);
        applySpeakersFromApi(updated, setSpeakers, playbackLockRef, readSpeakersFromMap);
        const coord = resolveCoordinatorSpeaker(updated.length ? updated : Object.values(mapaSonos), coordId);
        if (coord) setCoordinatorId(coord.id);
      } catch {
        await refreshSpeakers();
      } finally {
        setTransportBusy(false);
      }
    };
    if (!requirePinIfNeeded(coordinator.pin_required, coordId, () => { void runTransport(); })) return;
    await runTransport();
  };

  const handlePlayPlaylist = async (
    pl: SpotifyPlaylist,
    opts?: {
      trackIndex?: number;
      meta?: { title?: string; artist?: string; albumArt?: string | null };
      tracks?: SpotifyPlaylistTrackRow[];
    },
  ) => {
    if (pl.tracks != null && pl.tracks <= 0) {
      notify(t('musicSystem.playback.emptyPlaylist'), 'error');
      return;
    }

    if (destinationKind === 'local') {
      try {
        let parsed = opts?.tracks?.filter((tr) => Boolean(tr?.uri)) ?? [];
        if (!parsed.length) {
          const data = await getSpotifyPlaylistTracks(pl.id);
          const rawItems = extractSpotifyPlaylistTrackItems(data);
          parsed = rawItems.map(parseSpotifyPlaylistTrackRow).filter(Boolean) as SpotifyPlaylistTrackRow[];
        }
        if (!parsed.length) {
          notify(t('musicSystem.playback.emptyPlaylist'), 'error');
          return;
        }
        const items = parsed.map((tr) => buildLocalQueueItem({
          uri: tr.uri,
          title: tr.name,
          artist: tr.artists,
          image_url: tr.imageUrl,
          durationMs: tr.durationMs,
        }));
        const start = Math.max(0, Math.min(opts?.trackIndex ?? 0, items.length - 1));
        const nextQueue: LocalQueueState = {
          items,
          currentIndex: start,
          shuffle,
          repeat,
        };
        setLocalQueue(nextQueue);
        await playLocalIndex(start, nextQueue);
      } catch (e: any) {
        const detail = e?.response?.data?.detail || e?.message;
        const kind = localPlaybackErrorKind(e);
        if (kind === 'premium') notify(t('musicSystem.localPlayback.premiumRequired'), 'error');
        else if (kind === 'reauth') notify(t('musicSystem.localPlayback.reauthRequired'), 'error');
        else notify(detail || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }

    const targetId = coordinatorId ?? speakerId;
    const targetSpeaker = mapaSonos[targetId] || coordinator;
    const runPlay = async () => {
      if (opts?.tracks?.length) {
        const plId = extractSpotifyPlaylistId(pl.id) || extractSpotifyPlaylistId(pl.uri) || pl.id;
        rememberPlaylistTrackIndex(plId, opts.tracks);
      }
      const startIdx = Math.max(0, opts?.trackIndex ?? 0);
      const startTrack = opts?.tracks?.[startIdx];
      const optimisticMeta = opts?.meta ?? (startTrack
        ? { title: startTrack.name, artist: startTrack.artists, albumArt: startTrack.imageUrl }
        : (pl.image_url ? { albumArt: pl.image_url } : undefined));
      applyOptimisticPlayback(targetId, 'playing', optimisticMeta);
      transportLockUntilRef.current = Date.now() + 10000;
      try {
        await pauseLocalPlayback().catch(() => undefined);
        const updated = await playSpotifyOnSpeaker({
          playlist_uri: pl.uri,
          playlist_name: pl.name,
          playlist_image_url: pl.image_url,
          target_speaker_id: targetId,
          crossfade,
          track_offset: opts?.trackIndex ?? 0,
        });
        await patchMusicPreferences({ default_crossfade: crossfade, last_used_speaker_id: targetId });
        applySpeakersFromApi(updated, setSpeakers, playbackLockRef, readSpeakersFromMap);
        const coord = resolveCoordinatorSpeaker(updated.length ? updated : Object.values(mapaSonos), targetId);
        if (coord) setCoordinatorId(coord.id);
      } catch (e: any) {
        playbackLockRef.current = null;
        notify(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
        await refreshSpeakers();
      }
    };
    if (!requirePinIfNeeded(targetSpeaker?.pin_required, targetId, () => { void runPlay(); })) return;
    await runPlay();
  };

  const closePlaylistDetailView = useCallback(() => {
    setPlaylistDetail(null);
  }, []);

  const handlePlayPlaylistFromDetail = (
    pl: SpotifyPlaylist,
    trackIndex?: number,
    meta?: { title?: string; artist?: string; albumArt?: string | null },
    tracks?: SpotifyPlaylistTrackRow[],
  ) => {
    void handlePlayPlaylist(pl, { trackIndex, meta, tracks });
  };

  const handleAddPlaylistToQueue = async (pl: SpotifyPlaylist, tracks?: SpotifyPlaylistTrackRow[]) => {
    if (destinationKind === 'local') {
      try {
        let parsed = tracks?.filter((tr) => Boolean(tr?.uri)) ?? [];
        if (!parsed.length) {
          const data = await getSpotifyPlaylistTracks(pl.id);
          const rawItems = extractSpotifyPlaylistTrackItems(data);
          parsed = rawItems.map(parseSpotifyPlaylistTrackRow).filter(Boolean) as SpotifyPlaylistTrackRow[];
        }
        const additions = parsed.map((tr) => buildLocalQueueItem({
          uri: tr.uri,
          title: tr.name,
          artist: tr.artists,
          image_url: tr.imageUrl,
          durationMs: tr.durationMs,
        }));
        setLocalQueue((prev) => ({ ...prev, items: [...prev.items, ...additions] }));
        notify(t('musicSystem.search.addedToQueue'), 'success');
      } catch (e: any) {
        notify(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    const targetId = coordinatorId ?? speakerId;
    try {
      await queueAdd(targetId, { playlist_uri: pl.uri });
      notify(t('musicSystem.search.addedToQueue'), 'success');
    } catch (e: any) {
      notify(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  };

  const handlePlayTrackFromPlaylist = (
    trackUri: string,
    pl: SpotifyPlaylist,
    trackIndex: number,
    track: { name: string; artists: string; imageUrl: string | null },
  ) => {
    void trackUri;
    handlePlayPlaylistFromDetail(pl, trackIndex, {
      title: track.name,
      artist: track.artists,
      albumArt: track.imageUrl,
    });
  };

  const handleLiveDestinationApplied = useCallback(async (
    result: LiveDestinationApplyResult,
    appliedSpeakers?: SonosSpeaker[],
  ) => {
    playbackLockRef.current = null;
    volumeLockRef.current = null;
    liveDestLockUntilRef.current = Date.now() + 12000;
    transportLockUntilRef.current = Date.now() + 12000;

    if (result.kind === 'local') {
      try {
        if (coordinatorId) {
          try { await sonosControl(coordinatorId, 'pause'); } catch { /* ignore */ }
        }
        await transferToLocalDevice(false);
        setDestinationKind('local');
        savePlaybackDestinationKind('local');
        void patchMusicPreferences({ playback_destination_kind: 'local' }).catch(() => undefined);
        notify(t('musicSystem.localPlayback.pausedSonos'), 'success');
      } catch (e: any) {
        const detail = e?.response?.data?.detail || e?.message;
        const kind = localPlaybackErrorKind(e);
        if (kind === 'premium') notify(t('musicSystem.localPlayback.premiumRequired'), 'error');
        else if (kind === 'reauth') notify(t('musicSystem.localPlayback.reauthRequired'), 'error');
        else notify(detail || t('musicSystem.localPlayback.sdkNotReady'), 'error');
      }
      return;
    }

    const appliedCoordinatorId = result.coordinatorId;
    if (appliedSpeakers?.length) mergeSonosSpeakers(appliedSpeakers);
    try { await pauseLocalPlayback(); } catch { /* ignore */ }
    setDestinationKind('sonos');
    savePlaybackDestinationKind('sonos');
    if (appliedCoordinatorId == null) {
      setCoordinatorId(speakerId);
      void patchMusicPreferences({ playback_destination_kind: 'sonos' }).catch(() => undefined);
    } else {
      const sp = await refreshSpeakers();
      const coord = resolveCoordinatorSpeaker(sp, appliedCoordinatorId);
      setCoordinatorId(coord?.id ?? appliedCoordinatorId);
      await patchMusicPreferences({
        last_used_speaker_id: coord?.id ?? appliedCoordinatorId,
        playback_destination_kind: 'sonos',
      }).catch(() => undefined);
    }
  }, [refreshSpeakers, coordinatorId, notify, t, speakerId]);

  const applyVolumeLock = useCallback((
    targetIds: number[],
    volume: number,
    mute: boolean,
  ) => {
    volumeLockRef.current = {
      until: Date.now() + 10000,
      values: new Map(targetIds.map((id) => [id, { volume, mute }])),
    };
  }, []);

  const handleVolumeApplied = useCallback((
    volSpeakerId: number,
    volume: number,
    mute: boolean,
    groupSpeakerIds?: number[],
  ) => {
    if (destinationKind === 'local') {
      void setLocalVolume(mute ? 0 : volume / 100);
      return;
    }
    const targetIds = groupSpeakerIds?.length ? groupSpeakerIds : [volSpeakerId];
    applyVolumeLock(targetIds, volume, mute);
    const patch = targetIds.map((id) => ({ ...(mapaSonos[id] || { id } as SonosSpeaker), volume, mute }));
    mergeSonosSpeakers(patch);
    setSpeakers(readSpeakersFromMap());
  }, [applyVolumeLock, readSpeakersFromMap, destinationKind]);

  const handleCrossfadeChange = async (v: boolean) => {
    if (destinationKind === 'local') return;
    setCrossfade(v);
    await patchMusicPreferences({ default_crossfade: v }).catch(() => undefined);
  };

  const handleSeek = async (seconds: number) => {
    if (destinationKind === 'local') {
      try {
        await seekLocalPlayback(Math.max(0, Math.floor(seconds * 1000)));
      } catch {
        notify(t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!coordinator) return;
    const coordId = coordinator.id;
    const runSeek = async () => {
      seekLockRef.current = { until: Date.now() + 15000, coordinatorId: coordId, position: seconds };
      transportLockUntilRef.current = Date.now() + 8000;
      const posStr = `${Math.floor(seconds / 3600)}:${Math.floor((seconds % 3600) / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
      mergeSonosSpeakers([{ ...coordinator, track_position: posStr }]);
      setSpeakers(readSpeakersFromMap());
      try {
        const updated = await sonosControl(coordId, 'seek', seconds);
        applySpeakersFromApi(updated, setSpeakers, playbackLockRef, readSpeakersFromMap);
      } catch {
        await refreshSpeakers();
      }
    };
    if (!requirePinIfNeeded(coordinator.pin_required, coordId, () => { void runSeek(); })) return;
    await runSeek();
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        minHeight: 120,
        backgroundColor: '#000', backgroundImage: AMBIENT_GLOW,
      }}>
        <CircularProgress sx={{ color: '#2391FF' }} />
      </Box>
    );
  }

  const ipLabel = deviceIp ?? coordinator?.ip ?? mapaSonos[speakerId]?.ip;

  return (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: '#000', backgroundImage: AMBIENT_GLOW,
    }}>
      {showDeviceIp && ipLabel && (
        <Typography sx={{
          color: '#888',
          fontSize: 10,
          px: 1.5,
          pt: 0.5,
          pb: 0.25,
          flexShrink: 0,
          fontFamily: 'monospace',
        }}>
          {t('sonos.modal.deviceIp')}: {ipLabel}
        </Typography>
      )}

      <ExpandedPlayerSheet
        open
        embedded
        anchorControls={isHorizontal}
        onClose={() => undefined}
        coordinator={playerCoordinator}
        destinationSpeakers={destinationSpeakers}
        canControl={canControl}
        crossfade={crossfade}
        onCrossfadeChange={handleCrossfadeChange}
        showCrossfadeControl={destinationKind !== 'local'}
        onTransport={handleTransport}
        onSeek={handleSeek}
        onOpenDestination={openLiveDestination}
        onQueueWillOpen={() => {
          setPlaylistDetail(null);
          setLiveDestOpen(false);
          setPlaylistsOpen(false);
        }}
        onVolumeApplied={handleVolumeApplied}
        playingSourceLabel={playingSourceLabel}
        showPlaybackMedia={showPlaybackMedia}
        showPlaylistsButton
        onOpenPlaylists={() => setPlaylistsOpen(true)}
      />

      <Slide direction="up" in={playlistsOpen} mountOnEnter unmountOnExit>
        <Box
          data-audio-no-swipe=""
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 70,
            backgroundColor: '#000', backgroundImage: AMBIENT_GLOW,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.75,
            borderBottom: `1px solid ${SURFACE_BORDER}`,
            flexShrink: 0,
            background: HEADER_GRADIENT,
          }}>
            <Typography sx={{
              ...sonosPanelTitleSx,
              textAlign: 'left',
              flex: 1,
            }}>
              {t('musicSystem.playback.library')}
            </Typography>
            <IconButton onClick={requestClosePlaylists} sx={{ color: '#fff', p: 0.5 }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box sx={{ flexShrink: 0, px: 1.25, pt: 0.5 }}>
            <PlaybackSubTabs
              value={activeSubTab}
              onChange={handleSubTabChange}
              hasSpeaker={destinationKind === 'local' || Boolean(coordinator)}
              destCount={destinationSpeakers.length}
              onOpenCompactPlayer={openLiveDestination}
            />
          </Box>

          <Box
            ref={playlistScrollRef}
            data-audio-inner-scroll=""
            sx={{
              ...TOUCH_SCROLL_PANEL_SX,
              flex: 1,
              minHeight: 0,
              px: 1.25,
              pt: 0.5,
              pb: 2,
            }}
          >
            {subTab === 'playlists' && (
              <>
                <PlaylistLibraryToolbar
                  sortMode={playlistSortMode}
                  onSortModeChange={handleSortModeChange}
                  viewMode={playlistViewMode}
                  onViewModeChange={handleViewModeChange}
                  onAddPlaylist={() => setCreatePlaylistOpen(true)}
                  filterQuery={libraryFilterQuery}
                  onFilterQueryChange={setLibraryFilterQuery}
                  filterOpen={libraryFilterOpen}
                  onFilterOpenChange={(open) => {
                    setLibraryFilterOpen(open);
                    if (!open) setLibraryFilterQuery('');
                  }}
                />
                <SpotifyPlaylistGrid
                  variant="spotify"
                  showToolbar={false}
                  columns={2}
                  sortMode={playlistSortMode}
                  onSortModeChange={handleSortModeChange}
                  viewMode={playlistViewMode}
                  onViewModeChange={handleViewModeChange}
                  createOpen={createPlaylistOpen}
                  onCreateOpenChange={setCreatePlaylistOpen}
                  onOpenPlaylist={openPlaylistDetail}
                  onPlay={handlePlayPlaylist}
                  filterQuery={libraryFilterQuery}
                  refreshKey={libraryRefresh}
                />
                <SpotifySavedTracksSection
                  speakerId={coordinatorId}
                  crossfade={crossfade}
                  canControl={canControl}
                  onMessage={handleMessage}
                  refreshKey={libraryRefresh}
                  filterQuery={libraryFilterQuery}
                  onPlayUri={destinationKind === 'local' ? handleLocalPlayUri : undefined}
                  onQueueUri={destinationKind === 'local' ? handleLocalQueueUri : undefined}
                />
              </>
            )}
            {subTab === 'recent' && (
              <SpotifyRecentGrid variant="spotify" speakerId={coordinatorId} onPlay={handlePlayPlaylist} />
            )}
            {subTab === 'search' && (
              <SpotifySearchPanel
                speakerId={coordinatorId}
                crossfade={crossfade}
                canControl={canControl}
                onMessage={handleMessage}
                onLibraryChanged={() => setLibraryRefresh((k) => k + 1)}
                onPlayUri={destinationKind === 'local' ? handleLocalPlayUri : undefined}
                onQueueUri={destinationKind === 'local' ? handleLocalQueueUri : undefined}
                onPlayPlaylist={(uri, name) => handlePlayPlaylist({
                  uri, id: uri, name, image_url: null, tracks: null, owner: null,
                })}
              />
            )}
          </Box>

          <PlaylistDetailSheet
            open={Boolean(playlistDetail)}
            playlist={playlistDetail}
            onClose={closePlaylistDetailView}
            onPlayPlaylist={(pl) => { handlePlayPlaylistFromDetail(pl); }}
            onPlayTrack={handlePlayTrackFromPlaylist}
            onAddToQueue={(pl, tracks) => { void handleAddPlaylistToQueue(pl, tracks); }}
            onReplaceQueue={(pl, tracks) => { handlePlayPlaylistFromDetail(pl, undefined, undefined, tracks); }}
            onQueueTrackUri={destinationKind === 'local' ? async (tr) => {
              await handleLocalQueueUri(tr.uri, {
                title: tr.name,
                artist: tr.artists,
                image_url: tr.imageUrl,
                durationMs: tr.durationMs,
              });
            } : undefined}
            onPause={() => { void handleTransport('pause'); }}
            isPlaying={
              destinationKind === 'local'
                ? !localPlayer.paused && Boolean(localPlayer.track.uri)
                : isSonosPlaying(coordinator?.playback_state)
            }
            coordinator={playerCoordinator}
            destinationSpeakers={destinationKind === 'local' ? [] : destinationSpeakers}
            onOpenDestination={openLiveDestination}
            canPlay={(destinationKind === 'local' || Boolean(coordinatorId)) && canControl}
            canControl={canControl}
            onMessage={handleMessage}
            onConverted={() => setLibraryRefresh((k) => k + 1)}
            onDeleted={() => setLibraryRefresh((k) => k + 1)}
            onRenamed={(pl) => {
              setPlaylistDetail(pl);
              setLibraryRefresh((k) => k + 1);
            }}
          />
        </Box>
      </Slide>

      <SonosQueueOverlay
        open={queueOverlayOpen}
        onClose={handleQueueOverlayClose}
        speakerId={coordinatorId}
        coordinator={playerCoordinator}
        canControl={canControl}
        shuffle={shuffle}
        onShuffleChange={(v) => { void applyPlayMode(v, repeat); }}
        repeat={repeat}
        onRepeatChange={(mode) => { void applyPlayMode(shuffle, mode); }}
        panelOverride={destinationKind === 'local' ? (
          <LocalQueuePanel
            items={localQueue.items}
            currentIndex={localQueue.currentIndex}
            playing={!localPlayer.paused && localQueue.currentIndex >= 0}
            canControl={canControl}
            shuffle={shuffle}
            repeat={repeat}
            onShuffleChange={(v) => { void applyPlayMode(v, repeat); }}
            onRepeatChange={(mode) => { void applyPlayMode(shuffle, mode); }}
            onReorder={(from, to) => setLocalQueue((prev) => reorderLocalQueue(prev, from, to))}
            onRemove={(index) => setLocalQueue((prev) => removeLocalQueueItem(prev, index))}
            onClear={() => {
              void clearLocalPlayback();
              setLocalQueue(createEmptyLocalQueue());
            }}
            onPlayAt={(index) => { void playLocalIndex(index); }}
            onError={(msg) => handleMessage(msg, 'error')}
            onAddedToPlaylist={() => handleMessage(t('musicSystem.search.addedToPlaylist'), 'success')}
          />
        ) : undefined}
      />

      <LiveDestinationModal
        open={liveDestOpen}
        onClose={() => setLiveDestOpen(false)}
        speakers={speakers}
        initialCoordinatorId={coordinatorId}
        initialKind={destinationKind}
        allowLocal={false}
        restrictToHabitacionId={restrictToHabitacionId}
        onApplied={(result, appliedSpeakers) => {
          void handleLiveDestinationApplied(result, appliedSpeakers);
        }}
      />

      <SonosFeedbackSnackbar state={snackbar} onClose={closeSnackbar} />
    </Box>
  );
};

export default SonosModalPlayerView;
